import React, { useState, useEffect } from 'react';
import { Card, Row, Col, Spin, message } from 'antd';
import { SearchOutlined, CloseOutlined } from '@ant-design/icons';
import { useAuth0 } from '@auth0/auth0-react';
import { apiClient } from '../../utils/api';
import { theme } from '../../theme';

interface Padron {
  id: number;
  denominacion: string;
  nc?: string; // Agregar campo nc para compatibilidad
}

interface DatosFijos {
  id: number;
  id_empresa: number;
  id_sucursal: number;
  valor_string: string;
  alta: number;
}

interface CeldaEstado {
  casillero: string;        // Ejemplo: "A01"
  numero_ticket: string | null;  // Ejemplo: "12345" o null
  fecha_ticket: string | null;   // Ejemplo: "2024-03-21" o null
}

interface CeldaInfo {
  id: string;              // Ejemplo: "A01"
  ocupada: boolean;        // true/false
  numero_ticket: string | null;  // Ejemplo: "12345" o null
  fecha_ticket: string | null;   // Ejemplo: "2024-03-21" o null
}

const ListadoCeldas: React.FC = () => {
  const { getAccessTokenSilently, user, isLoading: authLoading, isAuthenticated } = useAuth0();
  
  // Agrupar todos los estados al inicio del componente
  const [loading, setLoading] = useState<boolean>(true);
  const [clientes, setClientes] = useState<Padron[]>([]);
  const [playa, setPlaya] = useState<DatosFijos[]>([]);
  const [clienteSeleccionado, setClienteSeleccionado] = useState<number | null>(null);
  const [desdeColumna, setDesdeColumna] = useState<string>('');
  const [hastaColumna, setHastaColumna] = useState<string>('');
  const [desdeFila, setDesdeFila] = useState<string>('');
  const [hastaFila, setHastaFila] = useState<string>('');
  const [userData, setUserData] = useState<{id_empresa?: number, id_sucursal?: number}>({});
  const [celdas, setCeldas] = useState<CeldaInfo[]>([]);
  
  // Estados para la búsqueda de cliente
  const [searchCliente, setSearchCliente] = useState('');
  const [showSearchCliente, setShowSearchCliente] = useState(false);
  const [selectedCliente, setSelectedCliente] = useState<Padron | null>(null);

  // Efecto para manejar la inicialización de datos del usuario
  useEffect(() => {
    if (isAuthenticated && user) {
      setUserData({
        id_empresa: user.id_empresa,
        id_sucursal: user.id_sucursal
      });
    }
  }, [isAuthenticated, user]);

  // Modificar fetchClientes para usar userData
  const fetchClientes = async () => {
    try {
      if (!userData.id_empresa) {
        console.log('Esperando datos de usuario...');
        return;
      }

      const token = await getAccessTokenSilently();
      const response = await apiClient.get<Padron[]>(
        `${process.env.REACT_APP_PADRON_URL}/leer_padron/${userData.id_empresa}`,
        { token }
      );

      setClientes(response.data);
      console.log('Clientes cargados:', response.data);
    } catch (error) {
      console.error('Error al cargar clientes:', error);
      message.error('Error al cargar lista de clientes');
    }
  };

  // Modificar fetchDatosPlaya
  const fetchDatosPlaya = async () => {
    try {
      if (!userData.id_empresa || !userData.id_sucursal) {
        console.log('Esperando datos de usuario...');
        return;
      }

      const token = await getAccessTokenSilently();
      const response = await apiClient.get<DatosFijos[]>(
        `${process.env.REACT_APP_RELACION_URL}/leer_datos_fijos/${userData.id_empresa}/${userData.id_sucursal}/PMR`,
        { token }
      );

      setPlaya(response.data);

      // Verificar cada condición por separado
      const hayDatos = response.data && response.data.length > 0;
      const primerElemento = hayDatos ? response.data[0] : null;

      if (!hayDatos || !primerElemento) {
        console.log('No hay datos de playa configurados');
        message.error('No hay datos de playa configurados');
        return;
      }

      // Verificar específicamente el campo alta
      if (primerElemento.alta !== 1) {
        console.log('Playa deshabilitada - Alta:', primerElemento.alta);
        message.warning('No se puede mostrar la playa: El control de la misma está de baja');
        return;
      }

      // Si llegamos aquí, alta es 1 y podemos procesar los datos
      const dato_playa = primerElemento.valor_string;
      console.log('Dato playa original:', dato_playa);
      
      // Separar por '='
      const [columnas, filas] = dato_playa.split('=').map(item => item.trim());
      console.log('Separación por =:', { columnas, filas });
      
      // Separar columnas por '-'
      const [desde_columna, hasta_columna] = columnas.split('-').map(item => item.trim());
      
      // Separar filas por '-'
      const [desde_fila, hasta_fila] = filas.split('-').map(item => item.trim());

      // Guardar en estados
      setDesdeColumna(desde_columna);
      setHastaColumna(hasta_columna);
      setDesdeFila(desde_fila);
      setHastaFila(hasta_fila);

      // Log detallado para control
      console.log('=== Datos de Playa Procesados ===');
      console.log('Columnas:', {
        desde: desde_columna,
        hasta: hasta_columna
      });
      console.log('Filas:', {
        desde: desde_fila,
        hasta: hasta_fila
      });
      console.log('============================');

    } catch (error) {
      console.error('Error al obtener datos de playa:', error);
      message.error('Error al cargar datos de playa');
    } finally {
      setLoading(false);
    }
  };

  // Modificar el useEffect principal
  useEffect(() => {
    const inicializarDatos = async () => {
      try {
        await fetchDatosPlaya();
        await fetchClientes();
      } catch (error) {
        console.error('Error en la inicialización:', error);
      }
    };

    if (userData.id_empresa && userData.id_sucursal) {
      console.log('Iniciando carga de datos con:', userData);
      inicializarDatos();
    }
  }, [userData]);

  // Efecto para cerrar el dropdown cuando se hace clic fuera
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (!target.closest('.cliente-search-container')) {
        setShowSearchCliente(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Mostrar loading mientras se cargan los datos de Auth0
  if (authLoading || !isAuthenticated) {
    return (
      <div style={{ padding: '24px' }}>
        <Card
          style={{ 
            backgroundColor: '#ffffff',
            borderRadius: theme.styles.borderRadius,
            boxShadow: theme.styles.boxShadow
          }}
        >
          <div style={{ textAlign: 'center', padding: '50px' }}>
            <Spin size="large" tip="Autenticando..." />
          </div>
        </Card>
      </div>
    );
  }

  // Función helper para generar rango de letras
  const generarRangoLetras = (desde: string, hasta: string): string[] => {
    const letras = [];
    let actual = desde.charCodeAt(0);
    const final = hasta.charCodeAt(0);
    
    while (actual <= final) {
      letras.push(String.fromCharCode(actual));
      actual++;
    }
    return letras;
  };

  // Agregar una función para generar todas las celdas vacías
  const generarCeldasVacias = (
    desdeCol: string, 
    hastaCol: string, 
    desdeFila: number, 
    hastaFila: number
  ): CeldaInfo[] => {
    const celdas: CeldaInfo[] = [];
    const columnas = generarRangoLetras(desdeCol, hastaCol);
    
    for (const columna of columnas) {
      for (let fila = desdeFila; fila <= hastaFila; fila++) {
        const casillero = `${columna}${fila.toString().padStart(2, '0')}`;
        celdas.push({
          id: casillero,
          ocupada: false,
          numero_ticket: null,
          fecha_ticket: null
        });
      }
    }
    return celdas;
  };

  // Modificar handleClienteChange
  const handleClienteChange = async (cliente: Padron) => {
    try {
      setClienteSeleccionado(cliente.id);
      setSelectedCliente(cliente);
      
      if (!desdeColumna || !hastaColumna || !desdeFila || !hastaFila) {
        message.error('No hay configuración de playa válida');
        return;
      }

      if (!userData.id_empresa || !userData.id_sucursal) {
        message.error('No hay datos de empresa/sucursal');
        return;
      }

      setLoading(true);

      // Generar todas las celdas como desocupadas
      const celdasVacias = generarCeldasVacias(
        desdeColumna.toUpperCase(),
        hastaColumna.toUpperCase(),
        parseInt(desdeFila),
        parseInt(hastaFila)
      );

      const token = await getAccessTokenSilently();
      const url = `${process.env.REACT_APP_RELACION_URL}/celdas_ocupadas/${cliente.id}/${userData.id_empresa}/${userData.id_sucursal}`;
      
      console.log('=== Request Info ===');
      console.log('URL:', url);
      console.log('Token presente:', !!token);

      const response = await apiClient.get<CeldaEstado[]>(url, { token });

      console.log('=== Respuesta del servidor ===');
      console.log('Status:', response.status);
      console.log('Data:', response.data);

      if (!Array.isArray(response.data)) {
        throw new Error('Formato de respuesta inválido');
      }

      // Crear un mapa de las celdas ocupadas para búsqueda rápida
      const celdasOcupadas = new Map(
        response.data.map(celda => [
          celda.casillero,
          {
            id: celda.casillero,
            ocupada: true,
            numero_ticket: celda.numero_ticket,
            fecha_ticket: celda.fecha_ticket
          }
        ])
      );

      // Actualizar las celdas vacías con la información de ocupadas
      const celdasFinales = celdasVacias.map(celda => 
        celdasOcupadas.get(celda.id) || celda
      );

      setCeldas(celdasFinales);
      console.log('Estado de celdas procesadas:', celdasFinales);

    } catch (error: any) {
      console.error('=== Error Detallado ===');
      console.error('Error:', error);
      console.error('Response:', error?.response);
      console.error('Response data:', error?.response?.data);
      console.error('Status:', error?.response?.status);
      
      let mensajeError = 'Error desconocido al cargar estado de celdas';
      
      if (error?.response?.data?.error) {
        mensajeError = error.response.data.error;
      } else if (error?.message) {
        mensajeError = error.message;
      }
      
      message.error(`Error al cargar estado de celdas: ${mensajeError}`);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div style={{ padding: '24px' }}>
        <Card
          style={{ 
            backgroundColor: '#ffffff',
            borderRadius: theme.styles.borderRadius,
            boxShadow: theme.styles.boxShadow
          }}
        >
          <div style={{ textAlign: 'center', padding: '50px' }}>
            <Spin size="large" tip="Cargando datos..." />
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div style={{ padding: '0' }}>
      {/* Título de la página */}
      <h1 style={{ 
        color: theme.colors.primaryDark, 
        fontSize: '28px', 
        fontWeight: 600, 
        marginBottom: '24px' 
      }}>
        Ocupación de Celdas
      </h1>

      <Card
        style={{ 
          backgroundColor: '#ffffff',
          borderRadius: theme.styles.borderRadius,
          boxShadow: theme.styles.boxShadow,
          maxWidth: '100%'
        }}
        styles={{
          header: {
            backgroundColor: theme.colors.primaryDark,
            color: 'white',
            borderRadius: '8px 8px 0 0',
            fontSize: '16px',
            fontWeight: 600
          }
        }}
        title="Información de Celdas"
        bodyStyle={{
          display: 'flex',
          flexDirection: 'column',
          height: 'calc(100vh - 200px)',
          padding: '24px'
        }}
      >
      {/* Selector de Cliente */}
      <Row justify="center" align="middle" gutter={[16, 16]} style={{ marginBottom: '24px' }}>
        <Col style={{ textAlign: 'right' }}>
          <span style={{ 
            fontWeight: 600, 
            marginRight: '8px',
            color: theme.colors.textPrimary,
            fontSize: '14px'
          }}>
            Cliente:
          </span>
        </Col>
        <Col span={8}>
          <div className="cliente-search-container" style={{ position: 'relative', width: '100%' }}>
            <input
              type="text"
              placeholder="Buscar por NC o denominación..."
              style={{
                width: '100%',
                paddingLeft: '30px',
                paddingRight: selectedCliente ? '30px' : undefined,
                paddingTop: '10px',
                paddingBottom: '10px',
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                marginBottom: '4px',
                backgroundColor: selectedCliente ? '#f9fafb' : 'white',
                cursor: 'pointer',
                fontSize: '14px'
              }}
              value={selectedCliente ? `${selectedCliente.nc || ''} - ${selectedCliente.denominacion}` : searchCliente}
              onChange={e => {
                setSearchCliente(e.target.value);
                setSelectedCliente(null);
                setShowSearchCliente(true);
              }}
              onFocus={() => setShowSearchCliente(true)}
              readOnly={!!selectedCliente}
            />
            {/* Lupa */}
            <SearchOutlined style={{
              position: 'absolute',
              left: '8px',
              top: '50%',
              transform: 'translateY(-50%)',
              color: '#666',
              pointerEvents: 'none',
              fontSize: '16px'
            }} />
            {/* Limpiar selección */}
            {selectedCliente && (
              <CloseOutlined
                style={{
                  position: 'absolute',
                  right: '10px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  color: '#666',
                  cursor: 'pointer',
                  fontSize: '16px',
                  zIndex: 1100
                }}
                onClick={() => {
                  setSelectedCliente(null);
                  setSearchCliente('');
                  setShowSearchCliente(true);
                  setClienteSeleccionado(null);
                  setCeldas([]);
                }}
                title="Limpiar selección"
              />
            )}
            {/* Dropdown */}
            {showSearchCliente && clientes.length > 0 && !selectedCliente && (
              <div style={{
                position: 'absolute',
                top: '100%',
                left: 0,
                right: 0,
                backgroundColor: 'white',
                border: '1px solid #ddd',
                borderRadius: '4px',
                maxHeight: '200px',
                overflowY: 'auto',
                zIndex: 1000,
                boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
              }}>
                {clientes.filter(c => {
                  const search = searchCliente.trim().toLowerCase();
                  return (
                    (c.nc && c.nc.toLowerCase().includes(search)) ||
                    c.denominacion.toLowerCase().includes(search)
                  );
                }).map(cliente => (
                  <div
                    key={cliente.id}
                    style={{
                      padding: '8px 12px',
                      cursor: 'pointer',
                      borderBottom: '1px solid #eee'
                    }}
                    onMouseOver={e => (e.currentTarget.style.backgroundColor = '#f5f5f5')}
                    onMouseOut={e => (e.currentTarget.style.backgroundColor = 'white')}
                    onClick={() => {
                      handleClienteChange(cliente);
                      setShowSearchCliente(false);
                    }}
                  >
                    {cliente.nc ? `${cliente.nc} - ${cliente.denominacion}` : cliente.denominacion}
                  </div>
                ))}
              </div>
            )}
          </div>
        </Col>
      </Row>

      {/* Grid de celdas */}
      {clienteSeleccionado && (
        <div style={{ 
          flex: 1,
          overflow: 'auto',
          padding: '10px'
        }}>
          <table style={{ 
            width: '100%',
            height: '100%',
            borderCollapse: 'separate',
            borderSpacing: '2px',
            tableLayout: 'fixed'
          }}>
            <thead style={{ position: 'sticky', top: 0, zIndex: 1 }}>
              <tr>
                <th style={{ 
                  backgroundColor: '#f0f0f0',
                  border: '1px solid #d9d9d9',
                  borderRadius: '4px',
                  minWidth: '40px'
                }}></th>
                {generarRangoLetras(desdeColumna, hastaColumna).map(letra => (
                  <th key={letra} style={{ 
                    backgroundColor: theme.colors.primaryDark,
                    color: 'white',
                    fontWeight: 600,
                    fontSize: '16px',
                    border: '1px solid #d9d9d9',
                    borderRadius: '4px',
                    padding: '8px'
                  }}>
                    {letra}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {Array.from(
                { length: parseInt(hastaFila) - parseInt(desdeFila) + 1 },
                (_, i) => (parseInt(desdeFila) + i).toString().padStart(2, '0')
              ).map(fila => (
                <tr key={fila}>
                  <td style={{ 
                    backgroundColor: theme.colors.primaryDark,
                    color: 'white',
                    fontWeight: 600,
                    fontSize: '16px',
                    border: '1px solid #d9d9d9',
                    borderRadius: '4px',
                    textAlign: 'center',
                    padding: '8px',
                    position: 'sticky',
                    left: 0
                  }}>
                    {fila}
                  </td>
                  {generarRangoLetras(desdeColumna, hastaColumna).map(columna => {
                    const celda = celdas.find(c => c.id === `${columna}${fila}`);
                    return (
                      <td 
                        key={`${columna}${fila}`}
                        style={{ 
                          backgroundColor: celda?.ocupada ? '#ff4d4f' : '#52c41a',
                          border: '1px solid #d9d9d9',
                          borderRadius: '4px',
                          cursor: 'pointer',
                          position: 'relative',
                          transition: 'all 0.3s',
                          textAlign: 'center',
                          aspectRatio: '1',
                          padding: 0
                        }}
                        onMouseEnter={(e) => {
                          const target = e.currentTarget;
                          target.style.transform = 'scale(1.05)';
                          target.style.zIndex = '1';
                        }}
                        onMouseLeave={(e) => {
                          const target = e.currentTarget;
                          target.style.transform = 'scale(1)';
                          target.style.zIndex = '0';
                        }}
                      >
                        <div
                          style={{
                            position: 'absolute',
                            bottom: '100%',
                            left: '50%',
                            transform: 'translateX(-50%)',
                            backgroundColor: 'rgba(0, 0, 0, 0.85)',
                            color: 'white',
                            padding: '12px 16px',
                            borderRadius: '4px',
                            fontSize: '14px',
                            whiteSpace: 'pre-line',
                            display: 'none',
                            zIndex: 2,
                            boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                            minWidth: '200px'
                          }}
                          className="tooltip-content"
                        >
                          <div style={{ 
                            borderBottom: '1px solid rgba(255,255,255,0.2)', 
                            marginBottom: '8px',
                            paddingBottom: '4px',
                            fontWeight: 'bold'
                          }}>
                            Casillero: {celda?.id}
                          </div>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                            <div>Estado: {celda?.ocupada ? 'OCUPADA' : 'LIBRE'}</div>
                            {celda?.ocupada ? (
                              <>
                                <div>Ticket: {celda.numero_ticket || 'No disponible'}</div>
                                <div>Fecha: {celda.fecha_ticket || 'No disponible'}</div>
                              </>
                            ) : (
                              <div style={{ color: '#52c41a' }}>Disponible para uso</div>
                            )}
                          </div>
                        </div>
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      </Card>
    </div>
  );
};

// Actualizar los estilos CSS
const styles = `
  td:hover .tooltip-content {
    display: block !important;
  }
  
  td {
    aspect-ratio: 1;
  }
  
  @media (max-width: 768px) {
    td, th {
      font-size: 12px !important;
      padding: 4px !important;
    }
  }
`;

// Agregar el elemento style al documento
const styleSheet = document.createElement("style");
styleSheet.innerText = styles;
document.head.appendChild(styleSheet);

export default ListadoCeldas;
